import _get from 'lodash/get';
import config from '@bedrock-rdk/config';
import { LAYOUT_ENTITIES, upFrontTokenService, layoutForThirdPartyService, userService } from '@bedrock/fido-sdk';
import { getCurrentTimeInSeconds } from '@bedrock-rdk/utils';
import { getShakaErrorInfo } from '@bedrock-rdk/media-engine';
import {
  getPlayerPauseAdFeature,
  getPlayerPauseAdVastFeature,
  getPlayerTrackPreferencesFeature,
} from '../features/features.selectors';
import { t } from '../../modules/i18n';
import { getUid } from '../user/user.selectors';
import { getCurrentRoute } from '../routing/routing.selectors';
import {
  getCurrentLayout,
  getRequestedEntityType,
  getRequestedEntityId,
  getCurrentSectionCode,
  getContentTemplatesOptions,
} from '../layout/layout.selectors';
import { prepareBlock } from '../layout/layout.utils';
import { getScreenSize } from '../app/app.selectors';
import { PAGE_STATE } from '../../modules/tornado/utils/virtualization';
import {
  getVideoSource,
  getSources,
  getCurrentSourceIndex,
  getIsLive,
  getAdJingleImageId,
  getAdJingleDurationInMilliseconds,
  getCurrentPauseAd,
  getPauseAdRequestData,
  getContentType,
  getMetadataItems,
  getSideGlassContent,
} from './player.selectors';
import { createItemAction } from '../../modules/cta/cta.actions';
import { getItemActionType } from '../../modules/cta/cta.utils';
import { fetchErrorMessage, getErrorLevel } from '../error/error.utils';
import CTA from '../../modules/cta/cta.constants';
import { handleLock } from '../lock/lock.actions';
import { errorAction } from '../error/error.actions';
import { ERROR_LEVELS, ERROR_TYPES } from '../error/error.constants';
import { setAttributes, sendEvent } from '../analytics/analytics.utils';
import { fetchPauseAd as fetchVastPauseAds } from './pauseAds/vastPauseAds.utils';
import { fetchPauseAd as fetchGamPauseAds } from './pauseAds/googlePauseAds.utils';
import { findItemByTimestamp, getMetadataItemsFromLayout } from './live.utils';
import {
  findCurrentVideoItem,
  isPlayerLayout,
  findForcedSubtitles,
  isForcedSubtitlesTrack,
  layoutAudioTrackToUsersPreference,
  layoutTextTrackToUsersPreference,
  selectNextPlayableSource,
} from './player.utils';
import {
  lockPlayer,
  setPlayerInitialized,
  adJingleStarted,
  adJingleComplete,
  SET_CURRENT_METADATA_ITEM,
  SET_AUDIO_TRACK,
  SET_SUBTITLE_TRACK,
  APPEND_NEW_METADATA_ITEMS,
  SIDE_GLASS_PAGE_REQUESTED,
  SIDE_GLASS_PAGE_LOADED,
  SIDE_GLASS_PAGE_FAILED,
  TRACK_PREFERENCES_CHANGED,
  setPauseAd,
  pauseAdsError,
  setPlayerError,
  switchToNextSource,
  PLAYER_ENTITIES,
} from './player.constants';

export const handleLayoutAction = (wrapper, type) => dispatch => {
  switch (type) {
    case CTA.LOCK: {
      dispatch(lockPlayer());
      dispatch(handleLock(wrapper, { routeBackOnClose: true }));

      break;
    }
    default: {
      dispatch(createItemAction(wrapper));

      break;
    }
  }
};

export const initialize = () => (dispatch, getState) => {
  const state = getState();

  const entityId = getRequestedEntityId(state);
  const layout = getCurrentLayout(state);
  const item = findCurrentVideoItem(layout, entityId);
  const actionType = getItemActionType(item);

  if (actionType === CTA.PLAY) {
    const entityType = getRequestedEntityType(state);

    dispatch(
      setPlayerInitialized({
        entityType,
        entityId,
        isLive: entityType === LAYOUT_ENTITIES.live,
        layout,
        item,
      }),
    );

    const source = getVideoSource(getState());
    if (!source) {
      dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.PLAYER, 'No sources available'));
    }
  } else {
    dispatch(handleLayoutAction({ item }, actionType));
  }
};

export const fetchPlayerLayout = () => async (dispatch, getState) => {
  const state = getState();
  const layout = getCurrentLayout(state);
  // NOTE: handling a case when a video layout loaded after user pressed Back button during loading
  // It means that a player screen tries to render non-player layout during transition between screens, so no additional handling is required
  if (isPlayerLayout(layout)) {
    const entityId = getRequestedEntityId(state);
    dispatch(initialize());

    try {
      const dimensions = _get(findCurrentVideoItem(layout, entityId), 'analytics.googleAnalytics');
      if (dimensions) {
        setAttributes(
          Object.entries(dimensions)
            .filter(([key]) => key.startsWith('dimension'))
            .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
        );
      }
    } catch (error) {
      dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.LAYOUT, 'No custom dimensions'));
    }
  }
};

export const fetchUpfrontToken = service => async (_, getState) => {
  const state = getState();

  const uid = getUid(state);
  // TODO: figure out if we should use the UID from DRM config
  // const uid = _get(source, 'drm.config.uid');
  const source = getVideoSource(state);
  const contentId = _get(source, 'drm.config.contentId');

  let token = '';
  if (contentId) {
    const isLive = getIsLive(state);
    const tokenRequestHandler = isLive ? upFrontTokenService.getLiveToken : upFrontTokenService.getVideoToken;
    const tokenAssetData = isLive ? { channelCode: contentId } : { videoId: contentId };

    token = await tokenRequestHandler.bind(upFrontTokenService)({
      user: { UID: uid },
      service,
      ...tokenAssetData,
    });
  }

  return { value: token, uid };
};

export const showAdJingle = () => (dispatch, getState) => {
  const state = getState();

  dispatch(adJingleStarted());

  if (!getAdJingleImageId(state)) {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    setTimeout(() => {
      resolve();
      dispatch(adJingleComplete());
    }, getAdJingleDurationInMilliseconds(state));
  });
};

export const switchToNextProgram = () => (dispatch, getState) => {
  const items = getMetadataItems(getState());
  const item = findItemByTimestamp(items, getCurrentTimeInSeconds());

  if (!item) {
    return;
  }

  const actionType = getItemActionType(item);

  if (actionType === CTA.PLAY) {
    dispatch({ type: SET_CURRENT_METADATA_ITEM, item });
  } else {
    dispatch(handleLayoutAction({ item }, actionType));
  }
};

export const getNewMetadataItems = () => async (dispatch, getState) => {
  const state = getState();
  const entityId = getRequestedEntityId(state);
  const entityType = getRequestedEntityType(state);
  const sectionCode = getCurrentSectionCode(state);

  const entity = { entityId, entityType, sectionCode };

  try {
    const data = await layoutForThirdPartyService.fetchByEntity(entity);
    const programs = getMetadataItemsFromLayout(data)
      .filter(item => item && item.itemContent)
      .map(({ itemContent, ...rest }) => ({ ...rest, ...itemContent }));

    if (programs.length) {
      dispatch({ type: APPEND_NEW_METADATA_ITEMS, programs, entityType, entityId });

      return dispatch(switchToNextProgram());
    }

    return dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.LAYOUT, 'No Live'));
  } catch (error) {
    return dispatch(errorAction(ERROR_LEVELS.FATAL, ERROR_TYPES.LAYOUT, error.message));
  }
};

/**
 * Redux thunk loading a page of the block displayed within the side glass panel.
 *
 * The panel content is kept within the player state on purpose: the layout controllers of the
 * player screens restart the playback on any modification of the layout cache, so the pagination
 * of the panel must not reach it
 *
 * @param {number} page zero-based page index
 * @returns {function} Redux action
 */
export const fetchSideGlassPage = page => async (dispatch, getState) => {
  const state = getState();
  const block = getSideGlassContent(state);

  if (!block || !block.id || _get(block, `pages[${page}]`) !== PAGE_STATE.MISSING) {
    return Promise.resolve();
  }

  const entityId = getRequestedEntityId(state);
  const entityType = getRequestedEntityType(state);
  const sectionCode = getCurrentSectionCode(state);

  dispatch({ type: SIDE_GLASS_PAGE_REQUESTED, page });

  let data;
  try {
    data = await layoutForThirdPartyService.fetchBlockPage({
      entityType,
      entityId,
      sectionCode,
      blockId: block.id,
      // NOTE: BFF API requires the pages to be 1-based
      page: page + 1,
    });
  } catch (error) {
    dispatch({ type: SIDE_GLASS_PAGE_FAILED, page });

    return dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.LAYOUT, error.message));
  }

  const entity = { entityType, entityId };
  const loadedBlock = prepareBlock(entity, getScreenSize(state), getContentTemplatesOptions(state))(data);

  return dispatch({
    type: SIDE_GLASS_PAGE_LOADED,
    page,
    items: _get(loadedBlock, 'content.items', []),
    totalItems: _get(loadedBlock, 'content.pagination.totalItems'),
  });
};

export const setAudioTrack = track => ({
  type: SET_AUDIO_TRACK,
  track,
});

export const setSubtitlesTrack = track => ({
  type: SET_SUBTITLE_TRACK,
  track,
});

export const changeTracksPreferences =
  ({ audioTrack, subtitlesTrack }) =>
  (dispatch, getState) => {
    dispatch({ type: TRACK_PREFERENCES_CHANGED, audioTrack, subtitlesTrack });

    const state = getState();
    const { originalLanguage, preferences } = state.player;
    const shouldTrackPreferencesAcrossDevices = getPlayerTrackPreferencesFeature(state);

    if (!shouldTrackPreferencesAcrossDevices || !originalLanguage) {
      return Promise.resolve();
    }

    return userService
      .setTrackPreferences({
        originalLanguage,
        audioPreference: layoutAudioTrackToUsersPreference(preferences.audioTrack),
        subtitlesPreference: preferences.textTrack ? layoutTextTrackToUsersPreference(preferences.textTrack) : null,
      })
      .catch(error => {
        dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.PLAYER, error.message));
      });
  };

export const selectAudioTrack = trackId => (dispatch, getState) => {
  const state = getState();
  const { audioTracks, selectedAudioTrack, subtitleTracks, selectedSubtitlesTrack } = state.player;

  if (selectedAudioTrack === trackId) {
    return;
  }

  const audioTrack = audioTracks.find(({ id }) => id === trackId);
  if (!audioTrack) {
    return;
  }

  dispatch(setAudioTrack(audioTrack));
  dispatch(changeTracksPreferences({ audioTrack }));

  const subtitlesTrack = subtitleTracks.find(({ id }) => id === selectedSubtitlesTrack);
  if (!subtitlesTrack || isForcedSubtitlesTrack(subtitlesTrack)) {
    const forcedSubtitlesTrack = findForcedSubtitles(subtitleTracks, audioTrack.language);
    dispatch(setSubtitlesTrack(forcedSubtitlesTrack || null));
  }
};

export const selectSubtitlesTrack = trackId => (dispatch, getState) => {
  const state = getState();
  const { subtitleTracks, audioTracks, selectedAudioTrack } = state.player;

  const subtitlesTrack = subtitleTracks.find(({ id }) => id === trackId);
  const audioTrack = audioTracks.find(({ id }) => id === selectedAudioTrack);

  // User selected to disable subtitles explicitly
  if (!trackId) {
    dispatch(changeTracksPreferences({ subtitlesTrack: null }));
  }

  if (subtitlesTrack) {
    dispatch(setSubtitlesTrack(subtitlesTrack));
    dispatch(changeTracksPreferences({ subtitlesTrack }));
  } else if (audioTrack) {
    const forcedSubtitlesTrack = findForcedSubtitles(subtitleTracks, audioTrack.language);
    dispatch(setSubtitlesTrack(forcedSubtitlesTrack));
  } else {
    dispatch(setSubtitlesTrack(null));
  }
};

export const refreshPauseAd = () => async (dispatch, getState) => {
  const state = getState();
  const currentPauseAd = getCurrentPauseAd(state);
  const currentAdReceiveTime = currentPauseAd ? currentPauseAd.receivedAt : 0;
  const timeoutInSeconds = Number.isFinite(config.ads.pauseAdRefreshTimeoutInSeconds)
    ? config.ads.pauseAdRefreshTimeoutInSeconds
    : 30;

  if (Date.now() - currentAdReceiveTime < timeoutInSeconds * 1000) {
    return Promise.resolve();
  }

  const adRequestData = getPauseAdRequestData(state);
  if (!adRequestData) {
    return Promise.resolve();
  }

  dispatch(setPauseAd(null));

  // NOTE: according to requirements, the VAST request itself should be delayed
  await new Promise(resolve => {
    setTimeout(resolve, 1000 * (adRequestData.brParams?.delayBeforeDisplay || 0));
  });

  let fetcher = null;
  if (getPlayerPauseAdVastFeature(state)) {
    fetcher = fetchVastPauseAds;
  } else if (getPlayerPauseAdFeature(state)) {
    fetcher = fetchGamPauseAds;
  }

  if (!fetcher) {
    return Promise.resolve();
  }

  return fetcher(adRequestData)
    .then(ad => {
      if (ad) {
        dispatch(setPauseAd({ ...ad, receivedAt: Date.now() }));
      } else {
        dispatch(pauseAdsError());
        dispatch(
          errorAction(
            ERROR_LEVELS.SILENT,
            ERROR_TYPES.LAYOUT,
            'No pause ad data: ad server returned an empty response',
          ),
        );
      }
    })
    .catch(error => {
      dispatch(pauseAdsError());
      dispatch(errorAction(ERROR_LEVELS.SILENT, ERROR_TYPES.LAYOUT, `No pause ad data: ${error.message}`));
    });
};

export const playbackError = error => (dispatch, getState) => {
  const state = getState();
  const level = getErrorLevel(error);

  // Source fallback: a fatal content error (start or mid-play) is retried on the next playable source until exhausted.
  if (level === ERROR_LEVELS.FATAL) {
    const currentSourceIndex = getCurrentSourceIndex(state);
    const startIndex = Number.isInteger(currentSourceIndex) ? currentSourceIndex + 1 : 0;
    const nextSourceIndex = selectNextPlayableSource({ sources: getSources(state), startIndex });

    if (nextSourceIndex >= 0) {
      // eslint-disable-next-line no-console
      console.log('[player.actions] Fatal playback error, falling back to source', nextSourceIndex);
      dispatch(switchToNextSource(nextSourceIndex));

      return;
    }
  }

  const path = getCurrentRoute(state);
  const entity = (path || '').split('/')[0];
  const message = fetchErrorMessage(error);
  const contentType = getContentType(state);

  // In case we are currently on a player screen, handle the error as usual, otherwise display a Snackbar (handled by MiniPlayer)
  dispatch(
    errorAction(
      PLAYER_ENTITIES.includes(entity) || level !== ERROR_LEVELS.FATAL ? level : ERROR_LEVELS.SILENT,
      ERROR_TYPES.PLAYER,
      message,
    ),
  );
  // The MiniPlayer is listening for this error, it will fire a notification event up on the view tree to the App instance,
  // also it will stop the playback visualization and handle audio player cleanup if needed
  dispatch(
    setPlayerError({
      level,
      message:
        contentType === LAYOUT_ENTITIES.audio || contentType === LAYOUT_ENTITIES.radio ? t('error.podcast') : message,
    }),
  );

  // Report the error to media analytics engines with its full metadata.
  const errorInfo = getShakaErrorInfo(error);
  setAttributes({
    errorCode: errorInfo.code,
    errorMessage: errorInfo.message,
    errorMetadata: errorInfo.metadata,
    errorFatal: errorInfo.fatal,
  });
  sendEvent({ key: 'playbackError' });
};
